// Vokshus bundle selector + free-shipping bar + sticky ATC.
// Hooks into snippets:
//   - vokshus-bundle-selector.liquid (radio-style picker)
//   - vokshus-shipping-bar.liquid    (progress bar)
//   - vokshus-sticky-atc.liquid      (mobile fixed bar)
//
// Behaviors:
//   1. Click on .vk-bundle__option toggles active state, syncs hidden form quantity
//   2. Updates add-to-cart button label with current bundle price
//   3. Updates shipping bar progress based on selected bundle price
//   4. Syncs sticky-ATC qty/price label
//   5. Mobile sticky-ATC visibility toggled by IntersectionObserver on first media item
//   6. Sticky-ATC button submits the main product form
//
// Free-shipping threshold reads from (in order):
//   1. shipping-bar data-vk-threshold attribute (populated by liquid from the SSOT inline block)
//   2. SSOT data-script `<script type="application/json" data-vokshus-data>` (vokshus-product-data.liquid)
//   3. FALLBACK_THRESHOLD constant below (defensive last resort — keep in sync with SSOT)

(() => {
  const FALLBACK_THRESHOLD = 39900;

  // Sticky-ATC submit: Safari <16 (iOS ≤15) saknar HTMLFormElement.requestSubmit.
  // Det är vanligt på 55+ sina äldre iPhones — utan fallback kastar knappen
  // TypeError och INGET händer ("trykker legg i kurv, skjer ingenting, prøvd 3 ganger").
  // form.submit() finns i alla browsers; formen är novalidate utan submit-handler,
  // så native submit → POST /cart/add → /cart är funktionellt identiskt.
  function submitFormCompat(form) {
    if (!form) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  // Scope shipping-bar lookup to the product section, since cart-summary
  // ALSO renders a [data-vk-shipping-bar] inside the cart drawer. Bare
  // document.querySelector returned cart-drawer's bar, leaving the visible
  // PDP bar stale.
  function productBar() {
    const scope = document.querySelector('.vk-product') || document;
    return scope.querySelector('[data-vk-shipping-bar]');
  }

  let ssotCache = null;
  function readSsotData() {
    if (ssotCache !== null) return ssotCache;
    try {
      const dataEl = document.querySelector('[data-vokshus-data]');
      ssotCache = dataEl ? JSON.parse(dataEl.textContent) : {};
    } catch (_e) {
      ssotCache = {};
    }
    return ssotCache;
  }

  function readThreshold() {
    const bar = productBar();
    if (bar) {
      const raw = parseInt(bar.dataset.vkThreshold, 10);
      if (Number.isFinite(raw) && raw > 0) return raw;
    }
    const ssot = readSsotData();
    if (Number.isFinite(ssot.threshold_minor) && ssot.threshold_minor > 0) {
      return ssot.threshold_minor;
    }
    return FALLBACK_THRESHOLD;
  }

  function formatNok(minor) {
    const major = Math.floor(minor / 100);
    return major.toLocaleString('nb-NO');
  }

  // Valuta-medveten formatering (2026-07-28, UK-öppningen).
  // window.VK_MONEY sätts av vokshus-product-form.liquid och bär besökarens
  // valuta plus kursen. Saknas den, eller är besökaren norsk, blir texten exakt
  // densamma som förut. Ett fel här får aldrig sudda ut priset.
  function moneyConfig() {
    const cfg = window.VK_MONEY;
    if (!cfg || !cfg.iso || cfg.iso === 'NOK') return null;
    return cfg;
  }

  function formatMoney(minor) {
    const cfg = moneyConfig();
    // «kr» och inte «NOK», samma enhet som snippets/vk-money.liquid skriver
    // server-side. Ändras den ena måste den andra ändras (Elias val 2026-08-14).
    if (!cfg) return `${formatNok(minor)} kr`;
    // 2026-08-19: ingen kursomräkning här längre. Alla data-vk-price/anchor och
    // data-vk-threshold är numera FÄRDIGVÄXLADE minsta enheter (bundle-selector
    // slår upp variantens riktiga presentment-pris). Kursen gånger SSOT-ören gav
    // «€45» på knappen när kassan tog €63,90 (Basis + block-kryss), eftersom
    // addon-krysset alltid burit färdigväxlat pris och summan blev en blandning.
    // 🔴 2026-08-21, sifferrevisionen: ETT PRIS AVRUNDAS ALDRIG. Raden nedan
    // löd förut Math.round(minor / 100), så köpknappen skrev «43 €» där kassan
    // tar 42,95. Jämna belopp skrivs fortfarande utan decimaler, så pundet står
    // kvar som «£24» och kronan som «449 kr». Samma regel som vokshus-addons.js
    // redan följde, och samma som snippets/vk-money.liquid gör server-side.
    const n = Math.round(minor) / 100;
    const sep = cfg.dec || ',';
    const num = Number.isInteger(n) ? `${n}` : n.toFixed(2).replace('.', sep);
    // cfg.enhetEfter sätts av snippets/vk-money-config.liquid och är enda
    // sanningen om enheten står före eller efter talet: «£37» men «420 kr».
    return cfg.enhetEfter ? `${num} ${cfg.symbol || ''}`.trim() : `${cfg.symbol || ''}${num}`;
  }

  // Besparingen skrivs «418 kr» på norska men «£37» i pund, därav egen variant.
  function formatSaving(minor) {
    const cfg = moneyConfig();
    if (!cfg) return `${formatNok(minor)} kr`;
    return formatMoney(minor);
  }

  function updateShippingBar(currentMinor) {
    const bar = productBar();
    if (!bar) return;
    const threshold = readThreshold();
    const remaining = Math.max(0, threshold - currentMinor);
    const pct = Math.min(100, (currentMinor / threshold) * 100);
    const fill = bar.querySelector('[data-vk-fill]');
    const remainingEl = bar.querySelector('[data-vk-remaining]');

    bar.dataset.vkState = remaining === 0 ? 'success' : 'pending';
    if (fill) fill.style.width = `${pct}%`;
    if (remainingEl) {
      remainingEl.textContent = formatMoney(remaining);
    }
  }

  function updateAddToCartButton(label) {
    const btn = document.querySelector('[data-vk-atc-button]');
    if (btn) btn.textContent = label;
  }

  // Blokk-krysset (Elias 6/8): köpknappen ska visa summan kit + blokk när krysset är i.
  // Priset (minor units) bärs av checkboxen via data-vk-price — redan marknadsväxlat live.
  function addonExtraMinor() {
    const box = document.querySelector('[data-vk-addon-blokk] input[type="checkbox"]');
    if (!box || !box.checked) return 0;
    const p = parseInt(box.dataset.vkPrice || '0', 10);
    return Number.isFinite(p) && p > 0 ? p : 0;
  }

  // 🔴 2026-08-23 kväll (Elias KÖR). Tre saker hör ihop och måste läsas tillsammans:
  //  1) Utanför Norge startar toppriset som ett FRÅN-pris på lösa påsen («Ab 28,95 €»),
  //     för Shopping-annonsen säljer den påsen och toppriset skrev Basis 42,95 €.
  //  2) Klickar kunden ett kombokort ska toppriset bli KORTETS pris och från-ordet försvinna.
  //  3) Stora köpknappen längst ned ska då köpa SAMMA paket, annars står två priser på
  //     samma sida igen — precis det vi håller på att ta bort.
  // Serverns huvudform pekar fortfarande på Basis. Det är no-JS-fallbacken och den regeln
  // står fast: Basis är radions default och huvudformens variant.
  // Norge är undantaget (Elias 23/8: «gör det där på alla sidor förutom Norge»).
  // Attributet sätts bara av sections/vokshus-product-form.liquid på icke-norska sidor.
  function dynamicTopEnabled() {
    return !!document.querySelector('[data-vk-dynamic-top]');
  }

  function clearFromPrice() {
    const el = document.querySelector('[data-vk-product-from]');
    if (el) el.style.display = 'none';
  }

  function inFromPriceMode() {
    const el = document.querySelector('[data-vk-product-from]');
    return !!el && el.style.display !== 'none';
  }

  // Kortets köpform ligger ANTINGEN inuti kortet (Kjøkkenstart, Familie, Storfamilie)
  // ELLER som syskon direkt efter (lösa påsen och Basis, som ligger utanför <label> så
  // klicket inte dubbeltriggar radion). Leta på båda ställena, annars köper knappen fel vara.
  function activeComboForm() {
    const card = document.querySelector('[data-vk-combo-card].vk-bundle__option--active');
    if (!card) return null;
    const inside = card.querySelector('form.vk-bundle__combo-form');
    if (inside) return inside;
    const next = card.nextElementSibling;
    if (next && next.classList && next.classList.contains('vk-bundle__combo-form')) return next;
    return null;
  }

  function updateTopPrice(priceMinor, anchorMinor) {
    const currentEl = document.querySelector('[data-vk-product-price]');
    const compareEl = document.querySelector('[data-vk-product-compare]');
    const sparEl = document.querySelector('[data-vk-product-spar]');
    const noteEl = document.querySelector('[data-vk-product-anchor-note]');

    clearFromPrice();
    if (currentEl) currentEl.textContent = formatMoney(priceMinor);

    // Anchor speglas dynamiskt från valt kit (Fersk = 897, Komplett = 1495), från SSOT-inline-assigns. Lös påse har ingen anchor.
    // Visa compare+SPAR endast så länge anchor > price (döljs annars). Spar i KR (heterogena kit).
    const hasAnchor = Number.isFinite(anchorMinor) && anchorMinor > priceMinor;
    if (hasAnchor) {
      const sparMinor = anchorMinor - priceMinor;
      if (compareEl) {
        compareEl.textContent = formatMoney(anchorMinor);
        compareEl.style.display = '';
      }
      if (sparEl) {
        // i18n: mal fra data-vk-spar-tpl. Fra 2026-09-16 har malen to deler delt av '|':
        // prosentdelen (med __PCT__) og krone-delen (med __AMT__), fordi boblen viser
        // prosenten stort og kronene som underrad. Gammel ett-delt mal virker fortsatt:
        // da finnes ingen '|' og hele strengen havner i prosentdelen, akkurat som for.
        const sparTpl = sparEl.dataset.vkSparTpl || 'Du sparer __AMT__';
        const sparPct = Math.floor(((anchorMinor - priceMinor) * 100) / anchorMinor);
        const sparParts = sparTpl.split('|');
        const sparBig = sparParts[0].replace('__PCT__', sparPct).replace('__AMT__', formatSaving(sparMinor));
        const sparSmall = sparParts.length > 1
          ? sparParts[1].replace('__PCT__', sparPct).replace('__AMT__', formatSaving(sparMinor))
          : '';
        // Boblen har egne barn for de to radene. Mangler de (gammel cachet HTML) faller
        // vi tilbake pa ren tekst, slik at et markup-bytte aldri tommer merket.
        const sparStrong = sparEl.querySelector('strong');
        const sparHint = sparEl.querySelector('.vk-value__hint');
        if (sparStrong) {
          sparStrong.textContent = sparBig;
          if (sparHint) sparHint.textContent = sparSmall;
        } else {
          sparEl.textContent = sparSmall ? `${sparBig} ${sparSmall}` : sparBig;
        }
        sparEl.style.display = '';
      }
      if (noteEl) noteEl.style.display = '';
    } else {
      if (compareEl) compareEl.style.display = 'none';
      if (sparEl) sparEl.style.display = 'none';
      if (noteEl) noteEl.style.display = 'none';
    }
  }

  function updateStickyAtc(label, priceMinor) {
    const stickyQty = document.querySelector('[data-vk-sticky-qty]');
    const stickyPrice = document.querySelector('[data-vk-sticky-price]');
    if (stickyPrice) {
      // Ny markup: namn (får trunkeras) + pris i egen span (trunkeras aldrig).
      if (stickyQty) stickyQty.textContent = label;
      stickyPrice.textContent = formatMoney(priceMinor);
    } else if (stickyQty) {
      // Äldre cachead HTML utan pris-span: behåll kombinerade texten.
      stickyQty.textContent = `${label} · ${formatMoney(priceMinor)}`;
    }
    // Fri frakt-badge: visa bara när valt kit når gratis-frakt-tröskeln (Lös påse 299 < 399 → dölj),
    // annars vore det ett falskt frakt-löfte. Samma tröskel-källa som shipping-baren.
    const stickyFreeship = document.querySelector('[data-vk-sticky-freeship]');
    if (stickyFreeship) stickyFreeship.hidden = priceMinor < readThreshold();
  }

  // Fasta knappen visar det kunden faktiskt köper (Elias 26/10 11:01 AKUT). Med Familiepakken förvald
  // stod lösa påsens bild och «Bivoks™ brødpose» bredvid paketpriset, och kunden läste 769/799 kr som
  // priset för en påse. Kombokort ⇒ paketets namn som rubrik och paketets bild (data-vk-thumb-<kortets id>
  // på baren; saknas den döljs bilden hellre än att visa fel vara). card = null ⇒ produktens rubrik och
  // den valda variantens bild (data-vk-thumb-v<id>), annars produktens (Elias 11:07: «dynamiskt»).
  function updateStickyProduct(card, variantId) {
    const bar = document.querySelector('[data-vk-sticky-atc]');
    if (!bar) return;
    const title = bar.querySelector('[data-vk-sticky-title]');
    const thumb = bar.querySelector('[data-vk-sticky-thumb]');
    const qty = bar.querySelector('[data-vk-sticky-qty]');
    if (title) {
      if (title.dataset.vkDefault === undefined) title.dataset.vkDefault = title.textContent;
      title.textContent = (card && card.dataset.vkLabel) || title.dataset.vkDefault;
    }
    if (qty) qty.hidden = !!card;
    if (thumb) {
      if (thumb.dataset.vkDefaultSrc === undefined) thumb.dataset.vkDefaultSrc = thumb.getAttribute('src') || '';
      const src = card
        ? (bar.getAttribute('data-vk-thumb-' + card.id) || '')
        : ((variantId && bar.getAttribute('data-vk-thumb-v' + variantId)) || thumb.dataset.vkDefaultSrc);
      if (src) thumb.setAttribute('src', src);
      thumb.hidden = !src;
    }
  }

  // Spar-forsterkning rett under CTA. Speiler valgt kit; skjules når anchor ≤ pris (lös påse).
  function updateAtcSavings(priceMinor, anchorMinor) {
    const wrap = document.querySelector('[data-vk-atc-savings]');
    if (!wrap) return;
    const amountEl = wrap.querySelector('[data-vk-atc-savings-amount]');
    const hasAnchor = Number.isFinite(anchorMinor) && anchorMinor > priceMinor;
    if (hasAnchor) {
      const sparMinor2 = anchorMinor - priceMinor;
      if (amountEl) amountEl.textContent = formatSaving(sparMinor2);
      wrap.hidden = false;
    } else {
      wrap.hidden = true;
    }
  }

  function updateQuantityInput(qty) {
    const input = document.querySelector('[data-vk-form-quantity]');
    if (input) input.value = qty;
  }

  function updateVariantInput(variantId) {
    if (!variantId) return;
    const input = document.querySelector('[data-vk-form-variant]');
    if (input) {
      input.value = variantId;
      // Synka Shopifys express-knapp (Apple/Google/Shop Pay): den läser varianten från
      // [name="id"] och uppdateras BARA på change-event (samma mönster som Dawn). Utan
      // detta fastnar express på den variant som låg i inputen vid sidladdning.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Fulfillment: _PackingList-propertyn måste följa valt kit — annars visar ordern
  // Fersk-innehåll när kunden bytt till Løs pose (SpeedFulfill packar efter denna).
  function updatePackingListInput(innhold) {
    const input = document.querySelector('[data-vk-form-packing]');
    if (input && innhold) input.value = innhold;
  }

  // Kit-innehåll-strip: visa bara delarna i valt kit + spegla rubrik. Kiten är nästlade
  // (kit0 ⊂ kit1 ⊂ kit2) → varje del har data-vk-kit-min; dölj om valt index < min.
  // Scope till .vk-product (kit-strip på hero/LP saknar väljare och rörs aldrig).
  function updateKitContents(option) {
    const scope = document.querySelector('.vk-product') || document;
    const kit = scope.querySelector('[data-vk-kit]');
    if (!kit) return;
    const idx = parseInt(option.dataset.vkOption, 10);
    if (!Number.isFinite(idx)) return;
    const label = option.querySelector('.vk-bundle__label')?.textContent.trim() || '';
    const titleEl = kit.querySelector('[data-vk-kit-title]');
    if (titleEl && label) {
      // i18n: mal fra data-vk-kit-title-tpl (| t pdp.kit_title_tpl); fallback norsk.
      const kitTpl = titleEl.dataset.vkKitTitleTpl || '__LABEL__ inneholder:';
      titleEl.textContent = kitTpl.replace('__LABEL__', label);
    }
    kit.querySelectorAll('[data-vk-kit-item]').forEach(item => {
      const min = parseInt(item.dataset.vkKitMin, 10);
      item.hidden = Number.isFinite(min) ? idx < min : false;
    });
  }

  // Det befintliga innehållsdragspel följer samma variant som köpvalet.
  // Kartan är serverrenderad från SSOT och lokaliserad; köpformulären rörs inte.
  function updatePackContents(variantId) {
    var contents;
    try {
      var scope = document.querySelector('.vk-pdp-under');
      if (!scope) return;
      var summary = scope.querySelector('[data-vk-pack-summary]');
      if (!summary) return;
      contents = summary.querySelector('[data-vk-pack-contents]');
      if (!contents) return;
      var mapEl = summary.querySelector('[data-vk-pack-summary-map]');
      var map = mapEl ? JSON.parse(mapEl.textContent || '{}') : {};
      var text = Object.prototype.hasOwnProperty.call(map, String(variantId)) ? map[String(variantId)] : '';
      if (typeof text === 'string' && text.trim()) {
        contents.textContent = text;
        contents.hidden = false;
        summary.hidden = false;
      } else {
        // En okänd variant får inte stå kvar med föregående variants innehåll.
        contents.textContent = '';
        contents.hidden = true;
        summary.hidden = true;
      }
    } catch (_) {
      // Fel i innehållskartan får aldrig avbryta variantval eller köp.
      if (contents) {
        contents.textContent = '';
        contents.hidden = true;
      }
      if (summary) summary.hidden = true;
    }
  }

  // Fyller kvantitets-rungens radioprick via klass — CSS:en får aldrig använda :has()
  // (gamla Safari, 55+-publiken), så JS äger tillståndet. Tom prick när inget chip är valt.
  function syncQtyRungDot() {
    document.querySelectorAll('.vk-bundle__rung--qty').forEach(rung => {
      rung.classList.toggle('vk-bundle__rung--picked', !!rung.querySelector('.vk-bundle__option--active'));
    });
  }

  // Diskret rörelse (Elias val 12/10, punkt 13): priset och texterna som byts vid paketval
  // tonar mjukt in. Bara opacitet, inget flyttar sig. Animationen (.vk-tona) ligger i
  // snippets/vokshus-bundle-selector.liquid och är av vid «minska rörelse».
  // try/catch: rörelse får aldrig stoppa ett köp.
  function tonaIn() {
    try {
      document.querySelectorAll('[data-vk-product-price], [data-vk-product-compare], [data-vk-product-spar], [data-vk-sticky-price], [data-vk-atc-savings], [data-vk-kit], [data-vk-pack-contents]').forEach(el => {
        el.classList.remove('vk-tona');
        void el.offsetWidth;
        el.classList.add('vk-tona');
      });
    } catch (e) { /* ingen rörelse hellre än ett fel */ }
  }

  function selectBundle(option, initial) {
    const fieldset = option.closest('[data-vk-bundle]');
    if (!fieldset) return;

    fieldset.querySelectorAll('.vk-bundle__option').forEach(o => {
      o.classList.remove('vk-bundle__option--active');
    });
    document.querySelectorAll('[data-vk-combo-card]').forEach(c => {
      c.classList.remove('vk-bundle__option--active');
    });
    option.classList.add('vk-bundle__option--active');
    syncQtyRungDot();
    // Radio valt ⇒ stora knappen säljer igen; kombokortens en-knapp-läge släpps (28/8).
    const infoEl = option.closest('.vk-product__info') || document.querySelector('.vk-product__info');
    if (infoEl) infoEl.classList.remove('vk-product__info--combo-active');

    const radio = option.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;

    const radioInput = option.querySelector('input[data-vk-price]');
    const price = parseInt(radioInput?.dataset.vkPrice || '0', 10);
    const anchor = parseInt(radioInput?.dataset.vkAnchor || '0', 10);
    const variantId = radioInput?.dataset.vkVariantId;
    const kitLabel = option.querySelector('.vk-bundle__label')?.textContent.trim() || '';

    updateVariantInput(variantId);
    updatePackingListInput(radioInput?.dataset.vkPacking);
    updateQuantityInput(1);
    // i18n: mal fra data-vk-atc-tpl (| t bundle.atc_label_price); fallback norsk literal.
    const atcBtn = document.querySelector('[data-vk-atc-button]');
    const atcTpl = (atcBtn && atcBtn.dataset.vkAtcTpl) || 'Legg i handlekurv __PRICE__';
    const btnPrice = price + addonExtraMinor();
    updateAddToCartButton(atcTpl.replace('__PRICE__', formatMoney(btnPrice)));
    updateShippingBar(price);
    updateStickyAtc(kitLabel, btnPrice);
    updateStickyProduct(null, variantId);
    // Vid sidladdning i från-läge lämnas toppriset i fred: servern har skrivit
    // «Ab 28,95 €» med flit och den här funktionen hade genast skrivit över det med Basis.
    if (initial !== true || inFromPriceMode() === false) {
      updateTopPrice(price, anchor);
      updateAtcSavings(price, anchor);
    }
    updateKitContents(option);
    updatePackContents(variantId);
    if (initial !== true) tonaIn();
    // Landhandelen 2026-08-10: huvudknapp/express/kit-strip är server-renderade hidden
    // (förvalet Familiepakken köps via kortets egen form) — avslöja vid första radio-valet.
    const mainWrap = document.querySelector('[data-vk-mainform]');
    if (mainWrap) mainWrap.hidden = false;
  }

  function initBundles() {
    const bundles = document.querySelectorAll('[data-vk-bundle]');
    if (!bundles.length) return;

    bundles.forEach(bundle => {
      // :not-guard (2026-07-19): combo-kort med egna /cart/add-forms kan numera ligga INUTI
      // fieldset (Familiepakken överst) — utan guarden binder initBundles preventDefault på dem
      // och selectBundle läser price=0 (radio saknas) → ATC-text «0 NOK» + blockerad köpknapp.
      bundle.querySelectorAll('.vk-bundle__option:not([data-vk-combo-card])').forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          selectBundle(option);
        });
        option.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectBundle(option);
          }
        });
        // Robusthet: lyssna också på radio change så vi täcker upp
        // om någon annan kod (eller browser) togglar radion direkt.
        const radio = option.querySelector('input[type="radio"]');
        if (radio) {
          radio.addEventListener('change', () => {
            if (radio.checked) selectBundle(option);
          });
        }
      });

      const checked = bundle.querySelector('input[type="radio"]:checked');
      if (checked) {
        const option = checked.closest('.vk-bundle__option');
        if (option) selectBundle(option, true);
      }
    });
  }

  // Kombo-korten (Komplett 599 / Stor pakke 799) ligger utanför fieldset[data-vk-bundle]
  // med egna /cart/add-forms — selectBundle ser dem aldrig. Utan denna sync står sticky-baren
  // kvar på radio-valet (449) och sticky-knappen köper FEL variant åt 599/799-väljare.
  function activateComboCard(card, initial) {
    document.querySelectorAll('[data-vk-bundle] .vk-bundle__option, [data-vk-combo-card]').forEach(o => {
      o.classList.remove('vk-bundle__option--active');
    });
    card.classList.add('vk-bundle__option--active');
    syncQtyRungDot();
    // Ett kombokort valt ⇒ CSS döljer stora knappen (en knapp i taget, Elias 28/8).
    // Nödvändigt på norska sidan: utan data-vk-dynamic-top säljer stora knappen
    // BASIS oavsett valt kort, så två synliga knappar var också en feilkjøpsfelle.
    const infoEl = card.closest('.vk-product__info') || document.querySelector('.vk-product__info');
    if (infoEl) infoEl.classList.add('vk-product__info--combo-active');
    const cPrice = parseInt(card.dataset.vkPrice || '0', 10);
    const cAnchor = parseInt(card.dataset.vkAnchor || '0', 10);
    updateStickyAtc(card.dataset.vkLabel || '', cPrice);
    updateStickyProduct(card);
    var comboIdInput = card.querySelector('.vk-bundle__combo-form [name="id"]');
    updatePackContents(comboIdInput && comboIdInput.value);
    if (initial !== true) tonaIn();
    if (!dynamicTopEnabled()) return;
    // 🔴 2026-08-23 kväll (Elias KÖR): toppriset och stora knappen följer kortet.
    // updateTopPrice döljer ankare och sparsiffra av sig själv när kortet saknar
    // ankare (lösa påsen), så inget falskt sparlöfte kan stå kvar.
    updateTopPrice(cPrice, cAnchor);
    updateAtcSavings(cPrice, cAnchor);
    updateShippingBar(cPrice);
    const atcBtn = document.querySelector('[data-vk-atc-button]');
    const atcTpl = (atcBtn && atcBtn.dataset.vkAtcTpl) || 'Legg i handlekurv __PRICE__';
    updateAddToCartButton(atcTpl.replace('__PRICE__', formatMoney(cPrice)));
  }

  function initComboCards() {
    document.querySelectorAll('[data-vk-combo-card]').forEach(card => {
      // Utan combo-form (mønster-rutnätet renderas bara med ≥3 köpbara mønster) får kortet
      // ALDRIG kombo-läge: sticky/knappen hade lovat Storfamilie men sålt huvudformens Basis.
      if (!card.querySelector('.vk-bundle__combo-form')) return;
      card.addEventListener('click', (e) => {
        if (e.target.closest('form')) return;
        activateComboCard(card);
      });
    });
  }

  // Familiepakken förvald (Elias 26/10 2026): kortet med data-vk-combo-default väljs vid
  // sidladdning, men bara när URL:en saknar ?variant= (en annonslänk till en viss variant
  // vinner) och kortets köpform finns. Servern markerar 2 stk, så faller JS bort säljer
  // stora knappen 2 stk precis som förut och ingen knapp lovar fel vara.
  function initComboDefault() {
    const card = document.querySelector('[data-vk-combo-card][data-vk-combo-default]');
    if (!card || !card.querySelector('.vk-bundle__combo-form')) return;
    try {
      if (/[?&]variant=/.test(window.location.search || '')) return;
    } catch (e) {
      return;
    }
    activateComboCard(card, true);
  }

  // Stora köpknappen längst ned. Är ett kombokort valt köper den KORTETS form, alltså
  // rätt variant, rätt mønster och rätt properties[_PackingList] — fulfillment läser den
  // raden. Utan det här hade knappen sagt 61,95 € och lagt Basis 42,95 € i korgen.
  // Inget kombokort valt ⇒ orörd, huvudformen postar Basis precis som förut.
  // Slutar JS fungera händer ingenting här och huvudformen postar Basis, som alltid.
  function initMainAtcFollowsCard() {
    const btn = document.querySelector('[data-vk-atc-button]');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      if (!dynamicTopEnabled()) return;
      const comboForm = activeComboForm();
      if (!comboForm) return;
      e.preventDefault();
      submitFormCompat(comboForm);
    });
  }

  // Kassa-knappen i mobilbaren (2026-08-22). Serverrenderad på cart.item_count, men
  // efter ett AJAX-köp ändras aldrig HTML:en — därför togglas den här också.
  function updateStickyCheckout(itemCount) {
    const link = document.querySelector('[data-vk-sticky-checkout]');
    if (link) link.hidden = !(itemCount > 0);
  }

  function initStickyAtc() {
    const stickyBar = document.querySelector('[data-vk-sticky-atc]');
    if (!stickyBar) return;

    // 🔴 En enda väg att visa baren: allt går via sync, så scrollen och observern
    // inte kan skriva över varandra. Har kunden valt paket stannar baren
    // framme (Elias 22/8: knappen ska finnas kvar när man tryckt på ett produktval).
    //
    // 🔴 2026-09-21: baren kom först när HELA hero-bilden lämnat rutan, mätt live till
    // scroll ~650 px på 390×844. Köprutans egen knapp ligger 1790 px ner, så kunden hade
    // ingen köpknapp alls under de första 650 pixlarna. Nu räcker en liten scroll.
    // Baren ligger fortfarande längst NER och är dold vid scroll 0, så första skärmen är
    // oförändrad: fotot dominerar och inget pris konkurrerar (Elias 23/8, ingen köpknapp
    // över fotot). CSS döljer baren helt från 768 px, så detta gäller bara mobil.
    const SHOW_AFTER_PX = 180;

    let lockedOpen = false;
    let pastHero = false;
    let pastScroll = false;
    const sync = () => {
      stickyBar.hidden = lockedOpen ? false : !(pastHero || pastScroll);
    };

    const heroMedia = document.querySelector('.vk-gallery__main, [data-vk-gallery] img, .vk-product__gallery img');

    if (heroMedia && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { pastHero = !entry.isIntersecting; });
        sync();
      }, { threshold: 0.1 });
      observer.observe(heroMedia);
    }

    // Scrollvakten är både den nya, tidigare utlösaren och reserven för när
    // IntersectionObserver aldrig hörs av (den fyrar inte i bakgrundsflikar och har
    // fallerat i äldre Safari). Utan den hade baren kunnat stanna dold för alltid,
    // eftersom markupen sätter hidden.
    const readScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      const nytt = y > SHOW_AFTER_PX;
      if (nytt === pastScroll) return;
      pastScroll = nytt;
      sync();
    };
    readScroll();
    window.addEventListener('scroll', readScroll, { passive: true });

    // Paketval ska ta fram baren direkt, oavsett var på sidan kunden befinner sig.
    // Köprutan i sidan ligger långt under paketkorten, och utan detta måste kunden
    // scrolla tillbaka för att komma åt en köpknapp.
    document.addEventListener('click', (e) => {
      const kort = e.target.closest('[data-vk-bundle] [data-vk-option], [data-vk-combo-card], .vk-bundle__option');
      if (!kort) return;
      lockedOpen = true;
      sync();
    }, { passive: true });

    // Kassa-knappen följer kurven. cart:update bär itemCount för TILLÄGGET, inte
    // totalen, så vi läser /cart.js när resursen saknas hellre än att räkna själva.
    document.addEventListener('cart:update', (e) => {
      const resurs = e.detail && e.detail.resource;
      if (resurs && typeof resurs.item_count === 'number') {
        updateStickyCheckout(resurs.item_count);
        return;
      }
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((c) => { if (c) updateStickyCheckout(c.item_count); })
        .catch(() => {});
    });

    const stickyBtn = stickyBar.querySelector('[data-vk-sticky-atc-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        // Kombo-kort valt → submitta DET formuläret: rätt variant + properties[_PackingList]
        // (fulfillment-kritiskt — ordern MÅSTE visa pose+bivoks-antalet).
        const comboForm = activeComboForm();
        if (comboForm) {
          submitFormCompat(comboForm);
          return;
        }
        const form = document.getElementById('vk-product-form') || document.querySelector('.vk-product__form');
        submitFormCompat(form);
      });
    }
  }

  // Mobilbaren (BORTTAGEN 23/8, vilande kod): CTA:n var ett ankare till kombokortet
  // (no-JS-fallback = scroll till kortet med native form). Med JS skickas kortets EGEN form
  // (#vk-stor-form, Storfamilie sedan 29/8) via submitFormCompat — mønster-selects +
  // _PackingList följer alltid med, och initAjaxAtc:s submit-lyssnare öppnar Kvitto-drawern.
  function initMobileOffer() {
    const btn = document.querySelector('[data-vk-mobile-offer-btn]');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      const comboFormEl = document.getElementById('vk-stor-form');
      if (comboFormEl) {
        e.preventDefault();
        submitFormCompat(comboFormEl);
      }
    });
  }

  // AJAX-ATC (2026-07-06): behåll kunden på PDP:n och låt Kvitto-drawern auto-öppna
  // (auto-open-attributet på <cart-drawer-component> via settings.auto_open_cart_drawer).
  // Vi återanvänder Horizons egen kedja: POST /cart/add.js med `sections` →
  // dispatcha 'cart:update' på document → cart-drawer öppnar, cart-items morphar,
  // cart-icon-bubblan räknar upp. FormData tar med properties[_PackingList] automatiskt.
  // Fallbacks: ingen drawer på sidan / nätverksfel → native submit (dagens beteende).
  // iOS ≤15: submitFormCompat faller till form.submit() som INTE triggar submit-event
  // → native POST → /cart — medveten graceful degradation.
  function initAjaxAtc() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer || !('fetch' in window)) return;

    // #vk-w3-form = 3-packens köpruta (kampanj 2026-08-03). Utan den här raden gör
    // knappen en native POST och kunden kastas till /cart, i stället för att kurvrutan
    // öppnar och post-ATC-kortet visas.
    const forms = document.querySelectorAll(
      '#vk-product-form, #vk-w3-form, .vk-bundle__combo-form, .vk-bundle__tier-form'
    );
    forms.forEach((form) => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        ajaxAddToCart(form);
      });
    });
  }

  // Kurv-cookie-vakten (2026-08-04): en kurv som skapades medan en marknad var tänd,
  // och som sedan släcktes, fastnar i sitt gamla sammanhang och svarar «er allerede
  // utsolgt» på ALLT, också varor utan lagerstyrning. Cookien lever i 14 dagar, så
  // kunden kommer aldrig loss av sig själv och ser en butik där inget går att köpa.
  // Vi känner igen svaret, kastar kurv-cookien och försöker EN gång till med en färsk
  // kurv. `localization` rörs ALDRIG: den bär kundens språk- och landval.
  const VK_CART_COOKIES = ['cart', 'cart_sig', 'cart_ts', 'cart_ver'];
  const VK_SOLD_OUT = /utsolgt|utsålt|slutsåld|sold out|ausverkauft/i;

  function looksSoldOut(json) {
    return VK_SOLD_OUT.test((json && (json.description || json.message)) || '');
  }

  function dropCartCookie() {
    const host = window.location.hostname;
    VK_CART_COOKIES.forEach((name) => {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.' + host;
    });
  }

  // Ett enda återförsök, aldrig fler: en riktigt slutsåld vara ska svara slutsåld.
  async function addWithCartGuard(fd) {
    const send = () => fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: fd,
    });
    let res = await send();
    let json = await res.json();
    if (res.status === 422 && looksSoldOut(json)) {
      dropCartCookie();
      res = await send();
      json = await res.json();
    }
    return { res: res, json: json };
  }

  let ajaxAtcBusy = false;
  async function ajaxAddToCart(form) {
    if (ajaxAtcBusy) return;
    ajaxAtcBusy = true;

    const button = form.querySelector('[type="submit"]');
    const originalLabel = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      // i18n: window.VK_I18N.bundleAdding (satt av vokshus-bundle-selector.liquid); fallback norsk.
      button.textContent = (window.VK_I18N && window.VK_I18N.bundleAdding) || 'Legger til …';
    }

    try {
      // Blokk-tillegget (2026-08-05, fraktregeln): kryssat = legg blokken FØRST, som egen
      // rad. Feil her får ALDRI stoppe hovedkjøpet — derfor egen try/catch.
      const addonBox = document.querySelector('[data-vk-addon-blokk] input[type="checkbox"]');
      if (addonBox && addonBox.checked) {
        try {
          const afd = new FormData();
          afd.append('id', addonBox.value);
          afd.append('quantity', '1');
          // Fulfillment: samma _PackingList-invariant som alla andra köpvägar (SSOT via data-attr).
          if (addonBox.dataset && addonBox.dataset.vkPacking) {
            afd.append('properties[_PackingList]', addonBox.dataset.vkPacking);
          }
          await addWithCartGuard(afd);
          addonBox.checked = false;
        } catch (addonErr) { /* hovedkjøpet fortsetter */ }
      }

      const fd = new FormData(form);
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      if (sectionIds.length) fd.append('sections', sectionIds.join(','));

      const attempt = await addWithCartGuard(fd);
      const res = attempt.res;
      const json = attempt.json;
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'add failed');

      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);

      // Samma event-form som Horizons CartAddEvent (assets/events.js) — plain Event
      // + manuell .detail eftersom bundlen inte är en modul och inte kan importera klassen.
      // OBS: form.getAttribute('id') — form.id skuggas av <input name="id"> (DOM-gotcha).
      const ev = new Event('cart:update', { bubbles: true });
      ev.detail = {
        resource: cart,
        sourceId: form.getAttribute('id') || 'vk-buy-form',
        data: {
          source: 'product-form-component',
          itemCount: parseInt(fd.get('quantity'), 10) || 1,
          variantId: String(fd.get('id') || ''),
          sections: json.sections || undefined,
        },
      };
      document.dispatchEvent(ev);

      // Auto-open-racet: cart-items morphar HELA header-sektionen synkront under
      // dispatchen — drawerns showDialog kan träffa en ersatt/stängd dialog. Öppna
      // explicit EFTER morphen (rAF) om auto-open inte hann/överlevde.
      requestAnimationFrame(() => {
        const dr = document.querySelector('cart-drawer-component');
        const dlg = dr && dr.querySelector('dialog');
        if (dr && typeof dr.showDialog === 'function' && !(dlg && dlg.open)) dr.showDialog();
      });
    } catch (err) {
      // Näts-/valideringsfel → native submit så köpet aldrig blockeras av AJAX-lagret.
      // prototype.submit.call: skyddar mot name="submit"-skuggning och triggar
      // INGEN submit-event (annars loopar vår egen listener).
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
      ajaxAtcBusy = false;
      HTMLFormElement.prototype.submit.call(form);
      return;
    }

    if (button) {
      button.disabled = false;
      button.textContent = originalLabel;
    }
    ajaxAtcBusy = false;
  }

  // Pakkebeskyttelse (2026-08-14): valbar 29 kr-rad i kurvsummeringen (sida + drawer).
  // Servern renderar kryssrutan `hidden` + checked ur cart.items; JS avslöjar den och
  // togglar varianten via /cart. Fel återställer bara krysset — kassan blockeras aldrig.
  let pkgProtBusy = false;

  function pkgProtSync() {
    const labels = document.querySelectorAll('[data-vk-pkgprot]');
    if (!labels.length) return;
    labels.forEach(function (el) { el.hidden = false; });
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || pkgProtBusy) return;
        document.querySelectorAll('[data-vk-pkgprot-cb]').forEach(function (box) {
          const vid = parseInt(box.dataset.vkPkgprotVid, 10);
          box.checked = cart.items.some(function (it) { return it.variant_id === vid; });
        });
      })
      .catch(function () {});
  }

  async function togglePkgProt(box) {
    if (pkgProtBusy) {
      box.checked = !box.checked;
      return;
    }
    pkgProtBusy = true;
    const wantOn = box.checked;
    box.disabled = true;
    try {
      const vid = box.dataset.vkPkgprotVid;
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map(function (el) { return el.dataset.sectionId; })
        .filter(Boolean);
      let json;
      if (wantOn) {
        const fd = new FormData();
        fd.append('id', vid);
        fd.append('quantity', '1');
        // Fulfillment: _PackingList-invarianten (SF packar efter raden; 'nothing to pack').
        if (box.dataset.vkPkgprotPacking) {
          fd.append('properties[_PackingList]', box.dataset.vkPkgprotPacking);
        }
        if (sectionIds.length) fd.append('sections', sectionIds.join(','));
        const attempt = await addWithCartGuard(fd);
        if (!attempt.res.ok || attempt.json.status) {
          throw new Error(attempt.json.description || attempt.json.message || 'add failed');
        }
        json = attempt.json;
      } else {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: String(vid), quantity: 0, sections: sectionIds.join(',') }),
        });
        json = await res.json();
        if (!res.ok) throw new Error('change failed');
      }

      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });

      // Samma event-form som ajaxAddToCart ovan: cart-items-component morphar på
      // detail.data.sections. cart:update auto-öppnar INTE drawern (den lyssnar på
      // CartAddEvent), så en toggle på cart-sidan poppar ingen drawer.
      const ev = new Event('cart:update', { bubbles: true });
      ev.detail = {
        resource: cart,
        sourceId: 'vk-pkgprot',
        data: {
          source: 'product-form-component',
          itemCount: wantOn ? 1 : 0,
          variantId: String(vid),
          sections: (json && json.sections) || undefined,
        },
      };
      document.dispatchEvent(ev);
    } catch (err) {
      box.checked = !wantOn; // tyst återställning — köpet får aldrig blockeras
    }
    box.disabled = false;
    pkgProtBusy = false;
    // Morphen kan ha ersatt kryssrutan (server-`hidden` + färskt checked-läge).
    requestAnimationFrame(pkgProtSync);
    setTimeout(pkgProtSync, 400);
  }

  function initPkgProt() {
    // Ingen existens-gate: vid tom kurv renderas raden först efter första köpet
    // (morph), så delegationen måste alltid stå redo.
    // Delegation: morphen byter ut noderna vid varje kurv-uppdatering,
    // direkta listeners hade tappats.
    document.addEventListener('change', function (e) {
      const box = e.target && e.target.closest && e.target.closest('[data-vk-pkgprot-cb]');
      if (box) togglePkgProt(box);
    });
    document.addEventListener('cart:update', function (e) {
      if (e.detail && e.detail.sourceId === 'vk-pkgprot') return;
      requestAnimationFrame(pkgProtSync);
      setTimeout(pkgProtSync, 400);
    });
    // Drawer-lådan HYDRERAS vid första öppningen (data-hydration-key): server-HTML:en
    // återställer `hidden` och inget cart:update fyras, så en manuellt öppnad låda
    // visade aldrig raden (hål från 14/8, hittat 29/8 vid pen-ombygget).
    // Re-synka på trigger-klicket, med eftersläng för hydreringens nätverksrunda.
    document.addEventListener('click', function (e) {
      const t = e.target && e.target.closest && e.target.closest('[data-testid="cart-drawer-trigger"]');
      if (!t) return;
      requestAnimationFrame(pkgProtSync);
      setTimeout(pkgProtSync, 400);
      setTimeout(pkgProtSync, 1200);
    });
    pkgProtSync();
  }

  function initGalleryDots() {
    const media = document.querySelector('.vk-product__media');
    const dots = document.querySelectorAll('[data-vk-dot]');
    const items = document.querySelectorAll('[data-vk-media-item]');
    if (!media || !dots.length || !items.length) return;
    if (!('IntersectionObserver' in window)) return;

    const itemsArr = Array.from(items);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
          const idx = itemsArr.indexOf(entry.target);
          if (idx === -1) return;
          dots.forEach(d => {
            d.classList.toggle('is-active', parseInt(d.dataset.vkDot, 10) === idx);
          });
        }
      });
    }, { root: media, threshold: [0.55] });

    itemsArr.forEach(item => observer.observe(item));
  }

  // #kjop-ankaret (2026-08-23). Startsidans hero-knapp länkar till
  // /products/brodpose#kjop så kunden landar på priset i stället för 624 px
  // ovanför det. Webbläsartestat: hoppet blir INTE av vid sidladdning, för
  // base.css:32 sätter `scroll-behavior: smooth` på <html> och Chrome hinner
  // inte fullfölja ett mjukt fragment-hopp medan galleriet fortfarande laddar.
  // Med `behavior: 'instant'` landar priset exakt på scroll-margin-top 96 px.
  // Har kunden redan börjat skrolla själv rör vi henne aldrig.
  function initHashJump() {
    if (location.hash !== '#kjop') return;
    const target = document.getElementById('kjop');
    if (!target || typeof target.scrollIntoView !== 'function') return;

    let userMoved = false;
    const markMoved = () => { userMoved = true; };
    ['wheel', 'touchstart', 'keydown'].forEach((ev) => {
      window.addEventListener(ev, markMoved, { once: true, passive: true });
    });

    const jump = () => {
      if (userMoved) return;
      try {
        target.scrollIntoView({ behavior: 'instant', block: 'start' });
      } catch (_) {
        // Äldre Safari på 55+ sina iPhones tar bara boolean-formen.
        target.scrollIntoView(true);
      }
    };

    jump();
    // Galleriets bilder laddas lazy och kan flytta priset efter första hoppet.
    window.addEventListener('load', jump, { once: true });
  }

  function init() {
    document.documentElement.classList.add('vk-bundle--js'); // rev40: CSS-gate — hopfällda väljarkort bara när JS finns
    initHashJump();
    initBundles();
    initComboCards();
    initComboDefault();
    initMainAtcFollowsCard();
    initStickyAtc();
    initMobileOffer();
    initAjaxAtc();
    initPkgProt();
    initGalleryDots();
    // Blokk-tillegget avsløres kun med JS: uten JS kan krysset ikke legge en ekstra
    // rad i kurven, så da skal det heller ikke synes (native-kjøpet må aldri blokkeres).
    document.querySelectorAll('[data-vk-addon-blokk]').forEach(function (el) {
      el.hidden = false;
      // Kryssets pris in i köpknappen (Elias 6/8): räkna om vald rad vid varje toggle.
      const box = el.querySelector('input[type="checkbox"]');
      if (box) {
        box.addEventListener('change', function () {
          const checked = document.querySelector('[data-vk-bundle] input[type="radio"]:checked');
          const option = checked && checked.closest('.vk-bundle__option');
          if (option) selectBundle(option);
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
